/**
 * heicTranscode — client-side HEIC/HEIF → JPEG transcode (spec 011, D3).
 *
 * Why: the backend's `strip_image_metadata` primitive (plan.md's D2 uniform
 * EXIF-strip pipeline) fails closed on HEIC — an untranscoded iPhone photo
 * would be silently rejected. `ensureJpeg` is called from the before/after
 * slots' file-pick handler before a HEIC file is ever handed to the photo
 * queue.
 *
 * Native-first, wasm-fallback: Safari (iOS/macOS — HEIC `<img>` display has
 * shipped since Safari 17) can decode HEIC through the plain
 * `createImageBitmap()` API. It delegates to the OS's own ImageIO codec, so
 * there's no wasm download, and it respects embedded EXIF/HEIF orientation
 * the same way the OS does — verified empirically against libheif's own
 * decode (byte-for-byte matching rotation on an oriented test HEIC across
 * both paths; see the PR description for the harness and results).
 * `transcodeHeicToJpeg` tries that path first and only falls back to
 * `heic2any` where `createImageBitmap` is unavailable or can't decode the
 * file — Chrome/Firefox/Edge have no HEIC codec and reject immediately
 * (verified: throws `InvalidStateError`), as does any HEIC variant the OS
 * codec doesn't handle but libheif does. iPhone is the dominant HEIC source
 * for this product, so this eliminates the wasm download for most pilot
 * users while keeping correctness for the rest — and because the fallback
 * triggers unconditionally on any failure, this path can only ever match or
 * beat the old heic2any-only behavior, never regress it.
 *
 * Deliberately NOT WebCodecs `ImageDecoder`: it only shipped in Safari 26,
 * and Safari doesn't reliably support `ImageDecoder.isTypeSupported` at all
 * (open WebKit gaps as of this writing). `createImageBitmap` is a far older,
 * more broadly deployed API and is what actually backs Safari's native HEIC
 * handling — no plugin/feature gate needed, it's a rendering-engine
 * capability like `<canvas>` itself, not a native device API.
 *
 * Bundle-size note: `heic2any` is only ever loaded via the dynamic `import()`
 * inside `transcodeHeicToJpegViaLib`, and that function is only reached
 * after both `isHeicFile` returns true AND the native path above declined
 * or failed. A user who never picks a HEIC file, or whose browser has
 * native support, never downloads this chunk. Do not hoist this import to
 * module scope.
 */

const HEIC_TYPES = new Set(['image/heic', 'image/heif']);
const HEIC_EXTENSION = /\.(heic|heif)$/i;
const JPEG_QUALITY = 0.85;

/**
 * True if `file` is (or looks like) HEIC/HEIF. Mirrors PhotoCapture's own
 * best-effort handling of the empty-`type` case some mobile browsers emit
 * for HEIC files — falls back to the file extension in that case.
 */
export function isHeicFile(file: File): boolean {
  if (HEIC_TYPES.has(file.type)) return true;
  if (file.type === '' && HEIC_EXTENSION.test(file.name)) return true;
  return false;
}

/** Renames a HEIC filename's extension to .jpg; appends one if there wasn't a match. */
function jpegFileName(originalName: string): string {
  if (HEIC_EXTENSION.test(originalName)) {
    return originalName.replace(HEIC_EXTENSION, '.jpg');
  }
  return `${originalName}.jpg`;
}

/**
 * Attempts the native `createImageBitmap` path. Returns `null` for any
 * reason the caller should fall back to `heic2any` instead — the API (or
 * `OffscreenCanvas`) is absent, the browser can't decode HEIC (rejects), or
 * canvas encoding fails. Never throws — a failure here must not surface as
 * a user-facing error, since the wasm fallback may still succeed.
 */
async function transcodeHeicViaCreateImageBitmap(file: File): Promise<File | null> {
  if (typeof createImageBitmap === 'undefined' || typeof OffscreenCanvas === 'undefined') {
    return null;
  }
  let bitmap: ImageBitmap | null = null;
  try {
    // Pin orientation semantics explicitly rather than relying on
    // per-engine defaults. `'from-image'` matches the behavior this
    // module's comment above claims (respect embedded EXIF/HEIF
    // orientation) — without it, a browser that decodes HEIC but
    // defaults to `'none'` would silently ship an unrotated JPEG, and
    // the canvas re-encode below permanently bakes that in (there's no
    // orientation tag left to recover downstream).
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0);
    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: JPEG_QUALITY });
    return new File([blob], jpegFileName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    return null;
  } finally {
    bitmap?.close();
  }
}

/**
 * Transcodes via `heic2any` (libheif-wasm, ~345KB gzip, dynamically
 * imported). The fallback path for browsers where `createImageBitmap` can't
 * handle HEIC — see the module-level comment.
 *
 * Throws on decode failure (e.g. a corrupt or unsupported HEIC variant) —
 * callers should treat that as a validation error, the same as any other
 * PhotoCapture rejection, not a silent pass-through.
 */
async function transcodeHeicToJpegViaLib(file: File): Promise<File> {
  const heic2any = (await import('heic2any')).default;
  const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: JPEG_QUALITY });
  // `multiple` is never passed, so this is always a single Blob, never Blob[].
  const blob = Array.isArray(result) ? result[0] : result;
  if (!blob) {
    throw new Error('HEIC transcode produced no output');
  }
  return new File([blob], jpegFileName(file.name), {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
}

/**
 * Transcodes a single HEIC/HEIF File to JPEG. Preserves `lastModified` —
 * the before/after suggestion (Slice 2) and the general album's `capturedAt`
 * derivation both read it, and it must survive the format change.
 *
 * Tries the native `createImageBitmap` path first (zero-download on
 * supporting browsers), falling back to `heic2any` otherwise. Throws only
 * if both paths fail — callers should treat that as a validation error, the
 * same as any other PhotoCapture rejection, not a silent pass-through.
 */
export async function transcodeHeicToJpeg(file: File): Promise<File> {
  const native = await transcodeHeicViaCreateImageBitmap(file);
  if (native) return native;
  return transcodeHeicToJpegViaLib(file);
}

/**
 * Returns `file` unchanged unless it's HEIC/HEIF, in which case it returns
 * the transcoded JPEG. The single entry point slot file-pick handlers call —
 * callers never need to check `isHeicFile` themselves.
 */
export async function ensureJpeg(file: File): Promise<File> {
  if (!isHeicFile(file)) return file;
  return transcodeHeicToJpeg(file);
}
